import type { JSX } from 'react'
import type { NavigateFunction } from 'react-router-dom'

import {
  UserIcon,
  SlidersVerticalIcon,
  BellIcon,
  Building2Icon,
  AllChannelsIcon,
  UsersIcon,
  CircleDollarSignIcon,
  TagIcon,
  FolderIcon,
  ClipboardListIcon,
  GripIcon,
  FlaskConicalIcon,
  MessageCircleHeartIcon,
  SettingsIcon,
  CodeXmlIcon,
  LayoutGridIcon,
} from '@buffer-mono/popcorn'

import type { OrganizationAction } from '~publish/contexts/AccountContext/graphqlEnums'
import { betaFeaturesRoute, referAFriendRoute } from '~publish/legacy/routes'

import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction } from './types'

/** What the actor can reach, as the Settings sidebar computes it. */
type SettingsAccess = {
  canAccessTeamFeatures: boolean
  showBetaFeatures: boolean
  organizationActions: readonly OrganizationAction[]
  hasConnectedChannels: boolean
}

type SettingItem = {
  path: string
  label: string
  keywords: string[]
  icon: JSX.Element
  end?: boolean
  /**
   * The same gate the Settings sidebar
   * (`pages/Settings/SettingsNavigationContent.tsx`) puts on this item. When
   * absent, every member gets the item.
   */
  isAvailable?: (access: SettingsAccess) => boolean
}

const hasAction =
  (action: OrganizationAction) =>
  ({ organizationActions }: SettingsAccess): boolean =>
    organizationActions.includes(action)

// Account settings
const ACCOUNT_SETTINGS: SettingItem[] = [
  {
    path: '/settings',
    label: 'Profile',
    keywords: ['profile', 'account', 'personal', 'name', 'email'],
    icon: <UserIcon />,
    end: true, // Match exact path
  },
  {
    path: '/settings/preferences',
    label: 'Preferences',
    keywords: ['preferences', 'settings', 'options', 'config'],
    icon: <SlidersVerticalIcon />,
  },
  {
    path: '/settings/notifications',
    label: 'Notifications',
    keywords: ['notifications', 'alerts', 'email', 'push'],
    icon: <BellIcon />,
  },
]

// Organization settings
const ORGANIZATION_SETTINGS: SettingItem[] = [
  {
    path: '/settings/general',
    label: 'General',
    keywords: ['general', 'organization', 'workspace', 'name'],
    icon: <Building2Icon />,
    isAvailable: hasAction('edit'),
  },
  {
    path: '/settings/channels',
    label: 'Channels',
    keywords: ['channels', 'accounts', 'social', 'connect'],
    icon: <AllChannelsIcon />,
    isAvailable: (access): boolean =>
      access.organizationActions.includes('manageChannels') ||
      access.hasConnectedChannels,
  },
  {
    path: '/settings/team',
    label: 'Team',
    keywords: ['team', 'members', 'users', 'invite', 'collaboration'],
    icon: <UsersIcon />,
    isAvailable: ({ canAccessTeamFeatures }): boolean => canAccessTeamFeatures,
  },
  {
    path: '/settings/billing',
    label: 'Billing',
    keywords: ['billing', 'payment', 'subscription', 'invoice', 'plan'],
    icon: <CircleDollarSignIcon />,
    isAvailable: hasAction('manageBilling'),
  },
]

// Feature settings
const FEATURE_SETTINGS: SettingItem[] = [
  {
    path: '/settings/tags',
    label: 'Tags',
    keywords: ['tags', 'labels', 'organize', 'categorize'],
    icon: <TagIcon />,
    isAvailable: hasAction('manageTags'),
  },
  {
    path: '/settings/channel-groups',
    label: 'Channel Groups',
    keywords: ['groups', 'channel groups', 'organize', 'bulk'],
    icon: <FolderIcon />,
  },
  {
    path: '/settings/saved-replies',
    label: 'Saved Replies',
    keywords: ['saved replies', 'templates', 'canned', 'responses'],
    icon: <ClipboardListIcon />,
    isAvailable: hasAction('manageSavedReplies'),
  },
]

// Other settings
const OTHER_SETTINGS: SettingItem[] = [
  {
    path: '/settings/apps-extras',
    label: 'Apps & Extras',
    keywords: ['apps', 'extras', 'integrations', 'extensions'],
    icon: <GripIcon />,
  },
  {
    path: betaFeaturesRoute.route,
    label: 'Beta Features',
    keywords: ['beta', 'features', 'experimental', 'labs'],
    icon: <FlaskConicalIcon />,
    isAvailable: ({ showBetaFeatures }): boolean => showBetaFeatures,
  },
  {
    path: referAFriendRoute.route,
    label: 'Refer a Friend',
    keywords: ['refer', 'friend', 'referral', 'invite'],
    icon: <MessageCircleHeartIcon />,
  },
  {
    path: '/settings/api',
    label: 'API',
    keywords: ['api', 'developer', 'keys', 'tokens', 'access', 'client'],
    icon: <CodeXmlIcon />,
  },
  {
    path: '/settings/integrations',
    label: 'Integrations',
    keywords: [
      'integrations',
      'agent',
      'connect',
      'apps',
      'third-party',
      'plugins',
    ],
    icon: <LayoutGridIcon />,
    isAvailable: hasAction('manageIntegrations'),
  },
]

const ALL_SETTINGS: readonly SettingItem[] = [
  ...ACCOUNT_SETTINGS,
  ...ORGANIZATION_SETTINGS,
  ...FEATURE_SETTINGS,
  ...OTHER_SETTINGS,
]

const isSettingAvailable = (
  setting: SettingItem,
  access: SettingsAccess,
): boolean => setting.isAvailable?.(access) ?? true

/**
 * Options for building settings-related actions.
 */
interface BuildSettingsActionsOptions {
  /** React Router navigate function for navigation */
  navigate: NavigateFunction
  /** Whether the user can access team features */
  canAccessTeamFeatures?: boolean
  /** Whether to show beta features settings */
  showBetaFeatures?: boolean
  /** The actor's allowed actions on the current organization */
  organizationActions?: readonly OrganizationAction[]
  /** Whether the organization has at least one connected channel */
  hasConnectedChannels?: boolean
  /** Current search query for collapsed/expanded behavior */
  query?: string
}

/**
 * Check if the query matches the keywords of a setting the actor can reach.
 * Returns true if we should show expanded settings.
 */
function shouldExpandSettings(query: string, access: SettingsAccess): boolean {
  if (!query.trim()) {
    return false
  }

  const normalizedQuery = query.toLowerCase().trim()

  // If query is just "settings" or "setting", stay collapsed
  if (normalizedQuery === 'settings' || normalizedQuery === 'setting') {
    return false
  }

  // Check if query matches any specific setting keyword
  return ALL_SETTINGS.filter((setting) => isSettingAvailable(setting, access))
    .flatMap((setting) => setting.keywords)
    .some(
      (keyword) =>
        keyword.includes(normalizedQuery) || normalizedQuery.includes(keyword),
    )
}

export function buildSettingsActions({
  navigate,
  canAccessTeamFeatures = false,
  showBetaFeatures = false,
  organizationActions = [],
  hasConnectedChannels = false,
  query = '',
}: BuildSettingsActionsOptions): CommandBarAction[] {
  const access: SettingsAccess = {
    canAccessTeamFeatures,
    showBetaFeatures,
    organizationActions,
    hasConnectedChannels,
  }

  // If no query or query doesn't match specific settings keywords,
  // return a single collapsed "Settings" action with comprehensive keywords
  // so it appears when searching for any settings-related term
  if (!shouldExpandSettings(query, access)) {
    return [
      createAction({
        id: 'settings',
        name: 'Settings',
        keywords: [
          'settings',
          'preferences',
          'account',
          'profile',
          'configuration',
          // Include high-level keywords from sub-items so Settings is discoverable
          'billing',
          'team',
          'notifications',
          'channels',
          'apps',
          'general',
          'tags',
          'api',
          'integrations',
        ],
        icon: <SettingsIcon />,
        section: 'settings',
        priority: PRIORITIES.settings,
        description: 'Open settings',
        perform: () => navigate('/settings'),
      }),
    ]
  }

  // Query matches specific settings - return expanded sub-items
  const groups: Array<{
    settings: SettingItem[]
    priority: number
  }> = [
    { settings: ACCOUNT_SETTINGS, priority: PRIORITIES.settings },
    { settings: ORGANIZATION_SETTINGS, priority: PRIORITIES.orgSettings },
    { settings: FEATURE_SETTINGS, priority: PRIORITIES.featureSettings },
    { settings: OTHER_SETTINGS, priority: PRIORITIES.otherSettings },
  ]

  return groups.flatMap(({ settings, priority }) =>
    settings
      .filter((setting) => isSettingAvailable(setting, access))
      .map((setting) =>
        createAction({
          id: `settings-${setting.path.replace(/\//g, '-')}`,
          name: setting.label,
          prefix: 'Settings',
          keywords: ['settings', ...setting.keywords],
          icon: setting.icon,
          section: 'settings',
          priority,
          perform: () => navigate(setting.path),
        }),
      ),
  )
}
